import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { IMAGES } from "@/lib/images";
import { CommercialCard } from "@/components/CommercialCard";
import { getProductImages } from "@/lib/productImages";
import { uploadedImageUrl } from "@/components/ImageUploader";
import { EnquiryDialog } from "@/components/EnquiryDialog";
import { Button } from "@/components/ui/button";
import { Ruler, Target, Droplets, Trophy } from "lucide-react";

const USES = [
  { icon: Trophy, title: "GAA & football pitches", copy: "Accurate, repeatable pitch markings for match and training." },
  { icon: Target, title: "Rugby & soccer", copy: "Full pitch and pitch-line marking for clubs and schools." },
  { icon: Ruler, title: "Courts & training grounds", copy: "Consistent lines across a range of surfaces and layouts." },
];

export default function LineMarking() {
  const [products, setProducts] = useState([]);
  const [enq, setEnq] = useState({ open: false, product: null });

  useEffect(() => {
    api.get("/products", { params: { category: "line_marking" } })
      .then((res) => setProducts(res.data)).catch(() => {});
  }, []);

  const CONSUMABLE_SLUGS = ["line-marking-paint-consumables", "baufan-supernova", "spectro-442"];
  const machines = useMemo(() => products.filter((p) => !CONSUMABLE_SLUGS.includes(p.slug)), [products]);
  const consumables = useMemo(() => products.filter((p) => CONSUMABLE_SLUGS.includes(p.slug)), [products]);

  const openEnquiry = (product) => setEnq({ open: true, product });

  return (
    <div>
      <section className="relative bg-brand-navy text-white">
        <div className="absolute inset-0 opacity-30"><img src={IMAGES.lineMarking} alt="" className="h-full w-full object-cover" /></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <span className="font-mono-spec text-xs uppercase tracking-widest text-white">Line Marking</span>
          <h1 className="mt-3 font-display font-extrabold tracking-tight text-4xl sm:text-5xl">Autonomous pitch & field marking</h1>
          <p className="mt-5 max-w-2xl text-lg text-white/80">GPS-guided line marking for GAA, soccer, rugby and school pitches. Details are being finalised — get in touch and we'll advise on the right system for your facility.</p>
          <Button onClick={() => setEnq({ open: true, product: null })} className="mt-8 bg-white hover:bg-white/90 text-brand-navy font-semibold" data-testid="linemarking-hero-enquire">Enquire about line marking</Button>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-brand-ink">Line marking systems</h2>
        <p className="mt-4 max-w-2xl text-lg text-muted-foreground">PaintMaster and FJD systems for autonomous field marking.</p>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {machines.map((p) => <CommercialCard key={p.slug} product={p} image={(p.uploaded_images?.[0] && uploadedImageUrl(p.uploaded_images[0])) || getProductImages(p.slug)[0] || IMAGES.lineMarking} onEnquire={openEnquiry} />)}
        </div>
      </section>

      {consumables.length > 0 && (
        <section className="bg-white border-y border-brand-navy/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
            <div className="flex items-center gap-3"><Droplets className="h-8 w-8 text-brand-navy" /><h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl text-brand-ink">Paint & consumables</h2></div>
            <p className="mt-4 max-w-2xl text-lg text-muted-foreground">Line-marking paint and consumables to keep your pitches game-ready.</p>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {consumables.map((p) => <CommercialCard key={p.slug} product={p} image={(p.uploaded_images?.[0] && uploadedImageUrl(p.uploaded_images[0])) || getProductImages(p.slug)[0] || IMAGES.grass} onEnquire={openEnquiry} />)}
            </div>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <h2 className="font-display font-bold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-brand-ink">Where it's used</h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
          {USES.map((u) => (
            <div key={u.title} className="rounded-2xl border border-brand-navy/10 bg-white p-6">
              <span className="grid place-items-center h-11 w-11 rounded-xl bg-brand-navy text-white"><u.icon className="h-5 w-5" /></span>
              <h3 className="mt-4 font-display font-semibold text-lg text-brand-navy">{u.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{u.copy}</p>
            </div>
          ))}
        </div>
      </section>

      <EnquiryDialog open={enq.open} onOpenChange={(o) => setEnq((e) => ({ ...e, open: o }))} type="line_marking" product={enq.product} sourcePage="line-marking" />
    </div>
  );
}
